// Converte um PDF (base64 ou jsPDF doc) em array de JPGs base64 (1 por pagina).
// Cliente nao precisa abrir PDF — JPG aparece direto no preview do balao do WhatsApp.
// Usa pdfjs-dist com worker carregado lazy via dynamic import (mesmo padrao do ThumbnailPdf).
import type { jsPDF } from 'jspdf';

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
function carregarPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then(async (mod) => {
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      (mod as any).GlobalWorkerOptions.workerSrc = workerUrl;
      return mod;
    });
  }
  return pdfjsPromise;
}

function base64ToUint8Array(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

function uint8ToBase64(u: Uint8Array): string {
  let s = '';
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]!);
  return btoa(s);
}

export interface PdfToJpegOptions {
  scale?: number;     // densidade do render. 2 = boa qualidade, ~150dpi. Default 2.
  quality?: number;   // JPEG quality 0..1. Default 0.85.
}

// Recebe PDF como ArrayBuffer ou base64. Retorna array de JPGs base64 (sem prefixo data:).
export async function pdfBase64ToJpegs(pdfBase64: string, opts: PdfToJpegOptions = {}): Promise<string[]> {
  const scale = opts.scale ?? 2;
  const quality = opts.quality ?? 0.85;
  const pdfjs = await carregarPdfjs();
  const data = base64ToUint8Array(pdfBase64);
  const pdf = await (pdfjs as any).getDocument({ data, disableAutoFetch: true, disableStream: true }).promise;
  const jpegs: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas sem contexto 2d');
    // Fundo branco — JPG nao tem alpha
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    jpegs.push(dataUrl.split(',')[1] ?? '');
    page.cleanup();
  }
  return jpegs;
}

// Conveniencia: recebe jsPDF doc e converte direto.
export async function jsPdfDocToJpegs(doc: jsPDF, opts: PdfToJpegOptions = {}): Promise<string[]> {
  const buf = doc.output('arraybuffer') as ArrayBuffer;
  const b64 = uint8ToBase64(new Uint8Array(buf));
  return pdfBase64ToJpegs(b64, opts);
}
